import { i18n } from '@/main'
import { useMeStore } from '@/stores/me'
import type { ContractRequest } from '@/types/contractRequests'
import { ArchivedContractStatuses } from '@/types/contractStatuses'
import type { Contract } from '@/types/contracts'
import type { Document, SupportingDocument } from '@/types/documents'
import { DocumentType } from '@/types/documents'
import { GtmEventType } from '@/types/gtmEvents'
import { ProductType } from '@/types/productTypes'
import { brokerIsStroobants } from '@/utils/brokers'
import { broker } from '@/utils/brokers'
import {
  contractRequestWithPendingDocument,
  contractRequestWithPendingPayment,
  contractWithPendingDocument,
  contractWithPendingPayment,
  contractWithPendingSignature,
  hasSelectedCivilLiabilityCover
} from '@/utils/business_logic'
import { contractFormParams } from '@/utils/business_logic'
import { filterStroobantsClaims } from '@/utils/claims'
import { isContractOrAmendmentDocument } from '@/utils/documents'
import { sendGtmEvent } from '@/utils/gtmEvent'
import type { NavigationItem } from '@/utils/navigation'
import { LinkType } from '@/utils/navigation'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import type { RequestState } from '@/utils/restStore'
import { isDocumentAvailable } from '@/utils/utils'
import { defineStore } from 'pinia'

import Api from '@/services/api'

import certificateIcon from '@/assets/svg/nav-icons/certificate.svg?component'
import contractsIcon from '@/assets/svg/nav-icons/contracts.svg?component'
import contractsNotificationIcon from '@/assets/svg/nav-icons/contracts_notification.svg?component'
import documentsIcon from '@/assets/svg/nav-icons/documents.svg?component'
import documentNotificationIcon from '@/assets/svg/nav-icons/documents_notification.svg?component'
import editIcon from '@/assets/svg/nav-icons/edit.svg?component'
import infoIcon from '@/assets/svg/nav-icons/info.svg?component'
import paiementIcon from '@/assets/svg/nav-icons/paiement.svg?component'
import paiementNotificationIcon from '@/assets/svg/nav-icons/paiement_notification.svg?component'

interface State {
  contract: RequestState<Contract>
  contractRequest: RequestState<ContractRequest>
  supportingDocuments: RequestState<SupportingDocument[]>
}

const initialState = (): State => ({
  contract: initialRequestState(),
  contractRequest: initialRequestState(),
  supportingDocuments: initialRequestState()
})

export const useContractStore = defineStore({
  id: 'contract',
  state: initialState,
  actions: {
    async fetchContract(uuid: String, type: String) {
      if (type === 'contract') {
        await handleRequest(
          () => Api.getContract(uuid),
          (contract: RequestState<Contract>) => {
            this.contract = contract
            this.contractRequest = initialState().contractRequest

            if (this.contract.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
              this.contract.data.claims = filterStroobantsClaims(this.contract.data.claims)
            }
          }
        )
      } else {
        await handleRequest(
          () => Api.getContractRequest(uuid),
          (contractRequest: RequestState<ContractRequest>) => {
            this.contractRequest = contractRequest
            this.contract = initialState().contract
          }
        )
      }
    },
    async fetchCompanyContract(companyId: string, contractId: string) {
      await handleRequest(
        () => Api.getCompanyContract(companyId, contractId),
        (contract: RequestState<Contract>) => {
          this.contract = contract
          this.contractRequest = initialState().contractRequest

          if (this.contract.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
            this.contract.data.claims = filterStroobantsClaims(this.contract.data.claims)
          }
        }
      )
    },
    async fetchContractSupportingDocuments(contractReference: string) {
      await handleRequest(
        () => Api.getContractSupportingDocuments(contractReference),
        (documents: RequestState<SupportingDocument[]>) => {
          this.supportingDocuments = documents
        }
      )
    }
  },
  getters: {
    documents: ({ contract, contractRequest }: State): Document[] => {
      let documents: Document[] = []
      if (contract.status === RequestStatus.RequestLoaded) documents = documents.concat(contract.data.documents)
      if (contractRequest.status === RequestStatus.RequestLoaded)
        documents = documents.concat(contractRequest.data.documents)

      return documents
    },
    contractSupportingDocuments: ({ supportingDocuments }: State): SupportingDocument[] => {
      let documents: SupportingDocument[] = []
      if (supportingDocuments.status === RequestStatus.RequestLoaded)
        documents = documents.concat(supportingDocuments.data)
      return documents
    },
    getLastAmendment: ({ contract, contractRequest }: State): Document | null => {
      if (contract.status === RequestStatus.RequestLoaded) return getLatestProcessedAmendment(contract.data.documents)
      else if (contractRequest.status === RequestStatus.RequestLoaded)
        return getLatestProcessedAmendment(contractRequest.data.documents)
      else return null
    },
    getContractDocument: ({ contract, contractRequest }: State): Document | null => {
      if (contract.status === RequestStatus.RequestLoaded) return getProcessedContractDocument(contract.data.documents)
      else if (contractRequest.status === RequestStatus.RequestLoaded)
        return getProcessedContractDocument(contractRequest.data.documents)
      else return null
    },
    filteredProcessedDocument(): Document[] {
      const documentToExclude = this.getLastAmendment || this.getContractDocument

      return this.documents.filter(
        (document) =>
          isDocumentAvailable(document) && isContractOrAmendmentDocument(document) && document !== documentToExclude
      )
    },
    lifeProceduredocuments: ({ contract }: State): Document[] => {
      return contract.status === RequestStatus.RequestLoaded
        ? contract.data.documents.filter((document) => document.type === DocumentType.LifeProcedure)
        : []
    },
    productType: ({ contract, contractRequest }: State): string => {
      if (contract.status === RequestStatus.RequestLoaded) return contract.data.productType
      if (contractRequest.status === RequestStatus.RequestLoaded) return contractRequest.data.productType

      return ''
    },
    contractNumber: ({ contract, contractRequest }: State): string | undefined => {
      if (contract.status === RequestStatus.RequestLoaded) return contract.data.reference
      if (contractRequest.status === RequestStatus.RequestLoaded) return contractRequest.data.reference

      return ''
    },
    contractNavigationItems: ({ contract }: State): NavigationItem[] => {
      if (contract.status !== RequestStatus.RequestLoaded) return []
      /** @ts-ignore */
      const $t = i18n.global.t
      const meStore = useMeStore()
      const gtmEventParams = {
        name: GtmEventType.ClickedOnModifyContract,
        category: 'contract',
        label: GtmEventType.ClickedOnModifyContract,
        value: { contract_id: 'id' }
      }

      const myWarrantiesItem: NavigationItem = {
        link: {
          type: LinkType.Route,
          route: {
            name: 'covers',
            params: { id: contract.data.uuid },
            query: { type: 'contract' }
          }
        },
        icon: infoIcon,
        i18nLabel: 'my_warranties'
      }

      const insuranceCertificateItem: NavigationItem = {
        link: {
          type: LinkType.Route,
          route: {
            name: 'insurance_certificates',
            params: { id: contract.data.uuid },
            query: { type: 'contract' }
          }
        },
        icon: certificateIcon,
        i18nLabel: 'insurance_certificate',
        hide: !contract.data.documents || !hasSelectedCivilLiabilityCover(contract.data)
      }

      const myPaiementsItem: NavigationItem = {
        link:
          contract.data.baloisePack && contract.data.chargebeePortalLink
            ? {
                type: LinkType.External,
                url: contract.data.chargebeePortalLink
              }
            : {
                type: LinkType.Route,
                route: {
                  name: 'invoices',
                  params: { id: contract.data.uuid },
                  query: { type: 'contract' }
                }
              },
        icon: contractWithPendingPayment(contract.data) ? paiementNotificationIcon : paiementIcon,
        i18nLabel: 'my_paiements',
        notification: contractWithPendingPayment(contract.data)
      }

      const contractDocumentsItem: NavigationItem = {
        link: {
          type: LinkType.Route,
          route: {
            name: 'contract_documents',
            params: { id: contract.data.uuid },
            query: { type: 'contract' }
          }
        },
        icon: contractWithPendingSignature(contract.data) ? contractsNotificationIcon : contractsIcon,
        i18nLabel: 'contract_documents',
        notification: contractWithPendingSignature(contract.data),
        hide: !contract.data.documents
      }

      const myDocumentsItem: NavigationItem = {
        link: {
          type: LinkType.Route,
          route: {
            name: 'contract_documents',
            params: { id: contract.data.uuid },
            query: { type: 'contract', contract_request_uuid: contract.data.contractRequestUuid }
          }
        },
        icon: contractWithPendingDocument(contract.data) ? documentNotificationIcon : documentsIcon,
        i18nLabel: 'my_documents',
        notification: contractWithPendingDocument(contract.data),
        hide: !contract.data.contractRequestUuid
      }

      const editContractUrl = () => {
        if (meStore.me.status === RequestStatus.RequestLoaded) {
          const link = $t('external_links.forms.edit_contract', {
            params: contractFormParams(contract.data, meStore.me.data, broker(), meStore.selectedCompanyId)
          })
          return link
        } else return ''
      }

      const editMyContractItem: NavigationItem = {
        link: {
          type: LinkType.External,
          url: editContractUrl()
        },
        icon: editIcon,
        i18nLabel: 'edit_my_contract',
        callback: () => sendGtmEvent(gtmEventParams)
      }

      const termsAndConditionsItem: NavigationItem = {
        link: {
          type: LinkType.External,
          url:
            localStorage.getItem('i18n-locale') === 'nl'
              ? 'https://app.sectorcatalog.be/SectorCatalog/public?language=NL&brokerkey=26727f94-bc41-4509-ac7a-7a682d82dcb6'
              : 'https://app.sectorcatalog.be/SectorCatalog/public?language=FR&brokerkey=26727f94-bc41-4509-ac7a-7a682d82dcb6'
        },
        icon: certificateIcon,
        i18nLabel: 'terms_and_conditions'
      }

      const isNotArchived = !ArchivedContractStatuses.includes(contract.data.status)
      const isCertificateEligibleProductType = [ProductType.Car, ProductType.Motorbike, ProductType.Boat].includes(
        contract.data.productType
      )

      const contractNavigationItems: NavigationItem[] = [
        ...(isNotArchived ? [myWarrantiesItem] : []),
        ...(isCertificateEligibleProductType ? [insuranceCertificateItem] : []),
        myPaiementsItem,
        contractDocumentsItem,
        myDocumentsItem,
        ...(isNotArchived ? [editMyContractItem] : []),
        termsAndConditionsItem
      ]

      return contractNavigationItems
    },
    contractRequestNavigationItems: ({ contractRequest }: State): NavigationItem[] => {
      if (contractRequest.status !== RequestStatus.RequestLoaded) return []

      const contractNavigationItems: NavigationItem[] = [
        {
          link: {
            type: LinkType.Route,
            route: {
              name: 'invoices',
              params: { id: contractRequest.data.uuid },
              query: { type: 'contract_request' }
            }
          },
          icon: contractRequestWithPendingPayment(contractRequest.data) ? paiementNotificationIcon : paiementIcon,
          i18nLabel: 'my_paiements',
          notification: contractRequestWithPendingPayment(contractRequest.data)
        },
        {
          link: {
            type: LinkType.Route,
            route: {
              name: 'contract_documents',
              params: { id: contractRequest.data.uuid },
              query: { type: 'contract_request' }
            }
          },
          icon: contractsIcon,
          i18nLabel: 'contract_documents'
        },
        {
          link: {
            type: LinkType.Route,
            route: {
              name: 'contract_documents',
              params: { id: contractRequest.data.uuid },
              query: { type: 'contract_request' }
            }
          },
          icon: contractRequestWithPendingDocument(contractRequest.data) ? documentNotificationIcon : documentsIcon,
          i18nLabel: 'my_documents',
          notification: contractRequestWithPendingDocument(contractRequest.data)
        },
        // {
        //   link: {
        //     type: LinkType.Route,
        //     route: {
        //       name: 'covers',
        //       params: { id: contractRequest.data.uuid },
        //       query: { type: 'contract_request' }
        //     }
        //   },
        //   icon: infoIcon,
        //   i18nLabel: 'my_warranties'
        // },
        {
          link: {
            type: LinkType.External,
            url:
              localStorage.getItem('i18n-locale') === 'nl'
                ? 'https://app.sectorcatalog.be/SectorCatalog/public?language=NL&brokerkey=26727f94-bc41-4509-ac7a-7a682d82dcb6'
                : 'https://app.sectorcatalog.be/SectorCatalog/public?language=FR&brokerkey=26727f94-bc41-4509-ac7a-7a682d82dcb6'
          },
          icon: certificateIcon,
          i18nLabel: 'terms_and_conditions'
        }
      ]

      return contractNavigationItems
    }
  }
})

export function getLatestProcessedAmendment(documents: Document[]): Document | null {
  return documents.reduce<Document | null>((latest, current) => {
    if (current.type !== DocumentType.Amendment || current.status !== 'processed') {
      return latest
    }

    const currentDate = current.additionalDetails?.enforcedAt
    if (!currentDate) return latest

    const latestDate = latest?.additionalDetails?.enforcedAt
    if (!latestDate) return current

    const currentSort = currentDate.split('/').reverse().join('')
    const latestSort = latestDate.split('/').reverse().join('')

    return currentSort > latestSort ? current : latest
  }, null)
}

export function getProcessedContractDocument(documents: Document[]): Document | null {
  return documents.reduce<Document | null>((latest, current) => {
    if (current.type !== DocumentType.Contract || current.status !== 'processed') {
      return latest
    }

    if (!latest || current.dateSortKey > latest.dateSortKey) {
      return current
    }

    return latest
  }, null)
}
